import type { StatusTone } from '@beonit/adaptive-raindrop';
import {
  isRunFinished,
  type AnalysisRun,
  type BatchEstimate,
  type Video,
  type VideoStatus,
} from '../model.js';

/**
 * What a video card says (HU-21, HU-22, HU-23).
 *
 * Pure functions, out here rather than in the components, because the `.vue` files of this
 * project are validated by hand in a browser and nothing with a rule in it should depend
 * on somebody remembering to look. What stays in a template is markup.
 */

/** Shown wherever a number is missing rather than zero. */
const NO_DATA = 'sin datos';

/**
 * How a video's state reads, and in which tone.
 *
 * The tones are the design system's — success, warning, danger, info, neutral — so a video's
 * state is coloured the same way a run's is on the queue screen. Picking a colour per screen
 * is how two screens end up disagreeing about what red means.
 */
export function videoStatusPresentation(status: VideoStatus): { label: string; tone: StatusTone } {
  switch (status) {
    case 'UPLOADING':
      return { label: 'Subiendo', tone: 'info' };
    // In the dataset and at rest: nothing analysed it yet, which is a state and not a fault.
    case 'READY':
      return { label: 'Listo', tone: 'neutral' };
    case 'ANALYSING':
      return { label: 'Analizando', tone: 'info' };
    case 'COMPLETED':
      return { label: 'Analizado', tone: 'success' };
    default:
      return { label: 'Con error', tone: 'danger' };
  }
}

/**
 * What a compact card writes on its frame about its state, beside the dot: only a failure, the one
 * state somebody has to act on. The dot's colour cannot be the only thing that tells it apart — not
 * on a touch screen, nor for whoever does not hover —; an analysis under way has its bar, and the
 * rest are states at rest.
 */
export function cardStateTag(status: VideoStatus): { label: string; tone: StatusTone } | null {
  return status === 'ERROR' ? { label: 'Error', tone: 'danger' } : null;
}

/**
 * A video's length, as mm:ss or h:mm:ss.
 *
 * `null` is «sin datos» and not «0:00». The duration is computed once when the video is
 * uploaded and never recomputed, so an older row simply has none — and a zero on a card
 * sends somebody looking for a broken file that is fine.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return NO_DATA;

  const total = Math.round(seconds);
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const remainder = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
  }

  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

/** Decimal units, Spanish decimal comma, one decimal. What a person reads, not what a disk says. */
export function formatFilesize(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes) || bytes <= 0) return NO_DATA;

  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;

  while (value >= 1_000 && unit < units.length - 1) {
    value /= 1_000;
    unit += 1;
  }

  const rounded = unit === 0 ? String(Math.round(value)) : value.toFixed(1).replace('.', ',');

  return `${rounded} ${units[unit]}`;
}

/**
 * How long something is expected to take.
 *
 * Minutes, because that is the unit somebody decides «not now» in. A `null` is «sin datos»
 * and stays that way: an invented number is worse than an empty space, because somebody
 * would plan around it.
 */
export function formatEstimate(milliseconds: number | null): string {
  if (milliseconds === null || !Number.isFinite(milliseconds) || milliseconds <= 0) return NO_DATA;

  const minutes = Math.round(milliseconds / 60_000);

  // Under a minute, said as such: «unos 0 min» reads as «instant» and this is not.
  if (minutes < 1) return 'menos de 1 min';
  if (minutes < 60) return `unos ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  return remainder === 0 ? `unas ${hours} h` : `unas ${hours} h ${remainder} min`;
}

/**
 * Whether the gallery still has anything that can change by itself.
 *
 * Two sources, and the second one is the verified trap of HU-23: **a group re-analysis
 * never touches the video's row**. A gallery that only watched the rows would stop polling
 * the moment every row read COMPLETED, and a video being re-analysed as part of a group
 * would sit there looking finished for the whole analysis.
 */
export function videosAreAlive(
  videos: readonly Video[] | undefined,
  liveRuns: readonly AnalysisRun[] = [],
): boolean {
  const rows = videos ?? [];

  if (rows.some((video) => video.status === 'UPLOADING' || video.status === 'ANALYSING')) {
    return true;
  }

  const onThisPage = new Set(rows.map((video) => String(video.id)));

  return liveRuns.some((run) => onThisPage.has(run.sourceId) && !isRunFinished(run.status));
}

export interface VideoLiveState {
  status: VideoStatus;
  progress: number;
  /** The worker's own step label, when a run is reporting one. */
  step: string | null;
  /** Set when what is running is a group's re-analysis, so the card can say whose it is. */
  groupId: string | null;
  /** The run the card is showing, or null when it is showing the row. */
  runId: string | null;
}

/**
 * What the card should show as happening.
 *
 * The ledger wins over the row. A group re-analysis leaves the row COMPLETED with progress
 * 100, so a card that believed the row would show a finished video while an analysis of it
 * is halfway through — and would offer no way to cancel the thing it cannot see.
 *
 * Among several live runs for the same video, the one that has started wins: the dispatcher
 * allows one group's re-analysis to be queued while another's runs, and a queued run has no
 * progress to show.
 */
export function liveStateOf(video: Video, liveRuns: readonly AnalysisRun[]): VideoLiveState {
  const own = liveRuns.filter(
    (run) => run.sourceId === String(video.id) && !isRunFinished(run.status),
  );
  const current = own.find((run) => run.startedAt !== null) ?? own[0];

  if (!current) {
    return {
      status: video.status,
      progress: video.progress,
      step: null,
      groupId: null,
      runId: null,
    };
  }

  return {
    status: 'ANALYSING',
    progress: current.progress,
    step: current.currentStep,
    groupId: current.groupId,
    runId: current.id,
  };
}

/**
 * How long the last analysis of a video took, in milliseconds.
 *
 * Read from the row's own two instants, which is all a listing has. `null` when either is
 * missing — the legacy subtracts them in JavaScript and paints a dash, which hides the
 * difference between «no tardó nada» and «no lo sabemos».
 */
export function lastAnalysisMs(video: Video): number | null {
  if (!video.analysisStartedAt || !video.analysisCompletedAt) return null;

  const span = video.analysisCompletedAt.getTime() - video.analysisStartedAt.getTime();

  return span > 0 ? span : null;
}

/**
 * The two dates a card shows, in the interface's language.
 *
 * Built once rather than per call: constructing an `Intl.DateTimeFormat` is the expensive part,
 * and a gallery of twenty-four cards asks for two each.
 */
const DAY = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

const MOMENT = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** When the video was uploaded. `null` where there is no date: never today's as a stand-in. */
export function formatUploadedOn(date: Date | null | undefined): string | null {
  return isRealDate(date) ? DAY.format(date) : null;
}

/**
 * When the last analysis finished, and how long it took.
 *
 * One chip rather than two: they are one fact — «esto se analizó entonces y costó esto» — and
 * splitting them across two chips made the card read as a list of unrelated numbers.
 *
 * The year is left out on purpose. A card is scanned, and «20 sept, 14:32» is read at a glance
 * where «20 sept 2026, 14:32» is parsed. The full date is in the tooltip for the rare case.
 */
export function formatAnalysedAt(video: Video): string | null {
  const at = video.analysisCompletedAt;

  if (!isRealDate(at)) return null;

  const took = lastAnalysisMs(video);

  return took === null ? MOMENT.format(at) : `${MOMENT.format(at)} · ${formatEstimate(took)}`;
}

/**
 * An `Invalid Date` is not a date.
 *
 * It reaches here as a real `Date` object whose time is `NaN` — the gateway builds one from
 * whatever string upstream sent, and a row with a malformed instant would otherwise print the
 * words «Invalid Date» on a card.
 */
function isRealDate(date: Date | null | undefined): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * How long re-analysing a selection would take, in one sentence.
 *
 * Both halves matter and the second is the reason this is not just a number. A video with no
 * history is **counted and left out of the total** rather than treated as zero, so a total on its
 * own would look complete while describing only part of the selection — and somebody would plan
 * around it and be wrong.
 */
export function estimateSummary(estimate: BatchEstimate): string {
  const total = formatEstimate(estimate.totalMs);
  const counted = estimate.videos.length - estimate.unknownVideos;

  if (estimate.unknownVideos === 0) return `${total} en total`;

  // Nothing at all to go on: a total of «unos 0 s» would be a lie dressed as an answer.
  if (counted <= 0) {
    return 'no disponible (sin análisis previos)';
  }

  return `${total} para ${counted} de ${estimate.videos.length}; de ${estimate.unknownVideos} no hay histórico`;
}

// ── The compact card ─────────────────────────────────────────────────────────

/** How much of a name with no space to cut at is kept at its end. */
const NAME_TAIL = 9;

/** A tail of words is at least this long — «2» alone tells nothing — and never much longer. */
const WORDS_TAIL_MIN = 5;
const WORDS_TAIL_MAX = 18;

/** A name shorter than this fits a card whole, and is never split. */
const NAME_SPLIT_FROM = 20;

/**
 * A name in two parts for a one-line card: the head gives way to an ellipsis, the tail always shows.
 *
 * On «Entrevista Ana — toma 2» the end is the only part that tells two cards apart, so the cut falls
 * in the middle rather than at the end. The tail is the last words — enough of them to say
 * something, «toma 2» and not «2» — or the last few characters of a file name with no spaces. The
 * space before the tail travels as a non-breaking one at the end of the head: a plain one there
 * would be trimmed, and the two parts would run together.
 */
export function cardNameParts(name: string): { head: string; tail: string } {
  if (name.length < NAME_SPLIT_FROM) return { head: name, tail: '' };

  for (let space = name.lastIndexOf(' '); space > 0; space = name.lastIndexOf(' ', space - 1)) {
    const length = name.length - space - 1;

    if (length > WORDS_TAIL_MAX) break;
    if (length >= WORDS_TAIL_MIN) {
      return { head: `${name.slice(0, space)}\u00a0`, tail: name.slice(space + 1) };
    }
  }

  return { head: name.slice(0, -NAME_TAIL), tail: name.slice(-NAME_TAIL) };
}

/**
 * What a compact card keeps for its tooltip, one line each: when it arrived, how its last analysis
 * stands — the progress and whose group's re-analysis it is, or where to read why it failed — when
 * that analysis finished, and its folder. A missing date or folder is said, never left out: a line
 * that is simply absent reads as one the card forgot.
 */
export function cardDetails(video: Video, live: VideoLiveState): string[] {
  const uploaded = formatUploadedOn(video.createdAt);
  const analysed = formatAnalysedAt(video);
  const state = [videoStatusPresentation(live.status).label];

  if (live.status === 'ANALYSING') {
    state.push(`${live.progress} %`);
    if (live.step) state.push(live.step);
    if (live.groupId) state.push(`reanálisis del grupo ${live.groupId}`);
  } else if (live.status === 'ERROR') {
    state.push('ábrelo para ver por qué falló');
  }

  return [
    uploaded ? `Subido el ${uploaded}` : 'Sin fecha de subida',
    state.join(' · '),
    ...(analysed ? [`Analizado el ${analysed}`] : []),
    video.folderName ?? 'Sin grupo',
  ];
}
